import { BrandingFields } from '@/components/events/BrandingFields';
import {
  brandingFromEntity,
  brandingToPayload,
  validateBranding,
  type BrandingFormData,
} from '@/lib/eventBranding';
import { useTemplateContext } from '@/pages/templates/useTemplateContext';
import {
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  Skeleton,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Palette, Save } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

const CARD_CLASSES = 'rounded-xl border bg-card shadow-sm py-0 gap-0';
const CARD_HEADER_CLASSES = 'px-6 py-4 !pb-4 border-b';

/**
 * Editable template branding tab. Owns the branding form state and saves it
 * directly (PATCH /templates/:id) so admins brand a template once and every
 * event created from it inherits the look. Hydrates from the template loaded
 * by the detail shell and refetches it on save. Mirrors EventBrandingTab.
 */
export function TemplateBrandingTab() {
  const { template, isLoading } = useTemplateContext();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<BrandingFormData | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Hydrate (and re-hydrate after a save refetch) from the loaded template.
  useEffect(() => {
    if (template) setForm(brandingFromEntity(template));
  }, [template]);

  const saveMutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) =>
      apiClient.patch(`/templates/${template?.id}`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['Template', template?.id] });
      queryClient.invalidateQueries({ queryKey: ['Template'] });
      toast.success('Branding saved');
    },
    onError: (err: Error) => toast.error(err?.message || 'Failed to save branding'),
  });

  // Dirty when the form diverges from the template's persisted branding.
  const isDirty = useMemo(() => {
    if (!template || !form) return false;
    return JSON.stringify(form) !== JSON.stringify(brandingFromEntity(template));
  }, [template, form]);

  if (isLoading || !form) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-32 rounded-xl" />
        <Skeleton className="h-48 rounded-xl" />
      </div>
    );
  }

  if (!template) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Template not found</p>
      </div>
    );
  }

  const updateField: <K extends keyof BrandingFormData>(key: K, value: BrandingFormData[K]) => void = (
    key,
    value,
  ) => {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
    // Clear the matching error(s) as the user edits.
    setErrors((prev) => {
      if (Object.keys(prev).length === 0) return prev;
      const next = { ...prev };
      delete next[key as string];
      if (key === 'challengeTypeIcons') {
        for (const k of Object.keys(next)) {
          if (k.startsWith('typeIconColor.')) delete next[k];
        }
      }
      return next;
    });
  };

  const handleSave = () => {
    const validationErrors = validateBranding(form);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      toast.error('Fix the highlighted color values before saving');
      return;
    }
    saveMutation.mutate(brandingToPayload(form));
  };

  const handleDiscard = () => {
    setForm(brandingFromEntity(template));
    setErrors({});
  };

  return (
    <div className="space-y-6">
      <Card className={CARD_CLASSES}>
        <CardHeader className={`${CARD_HEADER_CLASSES} flex flex-row items-center justify-between`}>
          <div>
            <CardTitle className="flex items-center gap-2">
              <Palette className="h-5 w-5" />
              Branding
            </CardTitle>
            <CardDescription>
              Logo, colors, theme, and map icons inherited by every event created from this template
            </CardDescription>
          </div>
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            {isDirty && (
              <Button variant="ghost" onClick={handleDiscard} disabled={saveMutation.isPending}>
                Discard
              </Button>
            )}
            <Button
              onClick={handleSave}
              loading={saveMutation.isPending}
              disabled={!isDirty}
              icon={Save}
            >
              Save Branding
            </Button>
          </div>
        </CardHeader>
      </Card>

      <BrandingFields value={form} errors={errors} onChange={updateField} />
    </div>
  );
}
